import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

interface FloatingMenuProps {
  position: { top: number; left: number };
  children: React.ReactNode;
  className?: string;
  targetRef?: React.RefObject<HTMLElement>;
}

export const FloatingMenu: React.FC<FloatingMenuProps> = ({
  position,
  children,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const menuRef = useRef<HTMLDivElement>(null);

  const onClose = () => setIsOpen(false);
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Close if click is outside menu and not on a trigger button
      if (menuRef.current && !menuRef.current.contains(target)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    // Small delay to prevent immediate close on open
    setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }, 0);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      className={className}
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: isOpen ? 10 : 5,
      }}
      onClick={() => setIsOpen(true)}
    >
      {children}
    </div>,
    document.body,
  );
};
